import type { SideLabel, Snapshot } from "../services/generated/client.js";
import type { TakenSlot } from "../components/game/Timeline.js";

/**
 * The gap the active side claims the card in flight belongs in — for one side.
 *
 * Returns `null` for every side that is not the one being contested, and for
 * every phase before the challenge window.
 *
 * # Why this is a lookup and not a phase check
 *
 * `RoundView.placement` is absent until `Phase::placement_is_public`, decided
 * server-side by one predicate. So asking *"is there a claim"* is the same
 * question as *"is the challenge window open"*, and the client never has to
 * know the phase table. A phase check here would be a second place that rule
 * lives, and the one that drifts — exactly the argument `outcomeFor` makes about
 * `Reveal`.
 *
 * # ⚠️ A claim is not the answer
 *
 * This is safe to render before `Reveal`, unlike an outcome. The slot says where
 * the active side *asserts* the card goes; the year stays server-side until the
 * reveal (Critical Rule 7). Phase 16a exists because no screen drew this, so the
 * challenge prompt asked people to contest something they had never seen.
 */
export function claimedSlotFor(board: Snapshot, sideId: string): number | null {
  const placement = board.round?.placement;
  if (!placement || placement.side !== sideId) {
    return null;
  }
  return placement.slot;
}

/**
 * Slots on this side's timeline that a challenger has claimed.
 *
 * **Phase 18, finding #9.** `challenged_slots` has been on the wire since 16b
 * and was rendered in exactly two places, both inside the *challenger's* own
 * panel and both gated on `canChallenge` — which excludes the active side by
 * definition. So the one person whose card was at stake could not see the claims
 * against them, and neither could the television.
 *
 * ⚠️ **The mirror of the finding that opened phase 16.** 16a made the placement
 * public so a challenger could aim; the same argument was never applied in the
 * other direction. Costs nothing on the wire — this is already published to
 * everybody, because slot-exclusivity is unplayable otherwise.
 *
 * 📌 **And it carries a name as of 20d.** *"'Somebody already claimed that gap'
 * should be 'Claimed by {name}'."* A `null` name means the roster does not know
 * that side — they left — which the screen says rather than inventing somebody.
 *
 * Empty for every side but the one being contested, and before the window opens.
 */
export function challengedSlotsFor(
  board: Snapshot,
  sideId: string,
  roster: readonly SideLabel[] = [],
): readonly TakenSlot[] {
  if (board.round?.placement?.side !== sideId) {
    return [];
  }
  return (board.round?.challenged_slots ?? []).map((claim) => ({
    slot: claim.slot,
    // Resolved here rather than in `Timeline`, which takes props and never a
    // roster: a component that has to look people up is a component that cannot
    // be rendered on its own.
    by: roster.find((entry) => entry.side === claim.side)?.name ?? null,
  }));
}

/**
 * What a side answered, as the words on the grids rather than as indices.
 *
 * **Phase 18, finding #1.** `SideView.guess` carries two indices into
 * `RoundView.artist_options` / `title_options`, which are already on the wire —
 * so this is a lookup rather than a second copy of the grids, the same argument
 * `claimedSlotFor` makes about the timeline.
 *
 * `null` before the challenge window (the server does not send it), and a `null`
 * *inside* the result means they never picked, which is a different sentence and
 * the screen says so.
 */
export function guessTextFor(
  board: Snapshot,
  sideId: string,
): { artist: string | null; title: string | null } | null {
  const side = board.sides.find((entry) => entry.id === sideId);
  const guess = side?.guess;
  if (!guess) {
    return null;
  }
  const round = board.round;
  const at = (
    options: readonly string[] | undefined,
    index: number | null | undefined,
  ) => (index === null || index === undefined ? null : (options?.[index] ?? null));
  return {
    artist: at(round?.artist_options, guess.artist_option),
    title: at(round?.title_options, guess.title_option),
  };
}

/**
 * Whether this side took the card by challenging rather than by placing well.
 *
 * **Phase 18, finding #13.** ⚠️ Deliberately not derived from `wins_card`: in a
 * concurrent mode several sides win cards in one round, so "won a card and did
 * not place well" is not the same statement. `challenge_won_by` is written once
 * by the engine, in the only place that knows (16j-2).
 */
export function wonByChallenge(board: Snapshot, sideId: string): boolean {
  return board.round?.challenge_won_by === sideId;
}
